import { V4UUID } from '@/firebase/database';
import { useGlobalStore } from '@/modules/globalModule/store';
import { useControlCenterStore } from '../../store';
import {
  CommandListStrategy,
  CommandResponse,
  ProcessResponseArg
} from './commandListStrategy';

export interface LaunchFile {
  name: string;
  path: string;
  pkgName: string;
  text: string;
  pkgId?: V4UUID;
}
export class LoadLaunchFilesStrategy extends CommandListStrategy {
  launchFiles: LaunchFile[] = [];
  skipLoaded: boolean = false;
  finalLoad: boolean = false;
  constructor(
    extraCommands: string,
    skipLoaded: boolean = false,
    finalLoad: boolean = false
  ) {
    super(extraCommands);
    this.finalLoad = finalLoad;
    this.commandList = [
      {
        command: 'ros2 pkg list',
        processResponse: this.processPkgList
      }
    ];
    this.skipLoaded = skipLoaded;
  }

  processPkgList(args: ProcessResponseArg): CommandResponse {
    args.strategy.addCommands(
      args.response
        .split('\n')
        // remove empty packages
        .filter((pkgName: string) => pkgName.trim() !== '')
        // filter out packages that already have loaded launch files if skipLoaded
        .filter((pkgName: string) => {
          return (
            !(args.strategy as LoadLaunchFilesStrategy).skipLoaded ||
            !useControlCenterStore().launchFiles.some(
              (launchFile: LaunchFile) => launchFile.pkgName === pkgName
            )
          );
        })
        .map((pkgName: string) => {
          return {
            command: `ros2 pkg prefix ${pkgName}`,
            processResponse: (args.strategy as LoadLaunchFilesStrategy)
              .processPkgPrefix,
            extraInfo: {
              pkgName: pkgName
            }
          };
        })
    );
    args.strategy.loadProgressFunc(
      'Loading Robot Launch files',
      (100 * args.strategy.currIndex) / args.strategy.commandList.length
    );
    return CommandResponse.SUCCESS;
  }

  processPkgPrefix(args: ProcessResponseArg): CommandResponse {
    if (
      args.response === 'Package not found' ||
      !args.extraInfo ||
      !args.extraInfo.pkgName
    )
      return CommandResponse.FAILURE;
    args.response = args.response.trim();
    args.strategy.addCommands([
      {
        command: `ls ${args.response}/share/${args.extraInfo.pkgName}/launch`,
        processResponse: (args.strategy as LoadLaunchFilesStrategy)
          .processLaunchLs,
        extraInfo: {
          pkgName: args.extraInfo.pkgName,
          path: `${args.response}/share/${args.extraInfo.pkgName}/launch`
        }
      }
    ]);
    args.strategy.loadProgressFunc(
      `Loading launch files from package ${args.extraInfo.pkgName}`,
      (100 * args.strategy.currIndex) / args.strategy.commandList.length
    );
    return CommandResponse.SUCCESS;
  }

  processLaunchLs(args: ProcessResponseArg): CommandResponse {
    if (!args.extraInfo || !args.extraInfo.pkgName || !args.extraInfo.path)
      return CommandResponse.SKIPPED;
    args.strategy.addCommands(
      args.response
        .split(/(\s+)/)
        .filter((launchFile: string) => {
          return launchFile.endsWith('.launch.py');
        })
        .map((launchFile: string) => {
          launchFile = launchFile.trim();
          return {
            command: `cat ${args.extraInfo.path}/${launchFile}`,
            processResponse: (args.strategy as LoadLaunchFilesStrategy)
              .processLaunchCat,
            extraInfo: { ...args.extraInfo, name: launchFile }
          };
        })
    );
    args.strategy.loadProgressFunc(
      `Loading launch files from package ${args.extraInfo.pkgName}`,
      (100 * args.strategy.currIndex) / args.strategy.commandList.length
    );
    return CommandResponse.SUCCESS;
  }

  processLaunchCat(args: ProcessResponseArg): CommandResponse {
    if (
      args.response ===
      `cat: ${args.extraInfo.path}/${args.extraInfo.name}: No such file or directory`
    )
      return CommandResponse.FAILURE;

    const globalStore = useGlobalStore();
    const pkg = globalStore.getPackageFromName(args.extraInfo.pkgName);
    (args.strategy as LoadLaunchFilesStrategy).launchFiles.push({
      name: args.extraInfo.name,
      path: args.extraInfo.path,
      pkgName: args.extraInfo.pkgName,
      text: args.response,
      pkgId: pkg ? pkg.id : undefined
    });
    args.strategy.loadProgressFunc(
      `Loading launch files from package ${args.extraInfo.pkgName}`,
      (100 * args.strategy.currIndex) / args.strategy.commandList.length
    );
    return CommandResponse.SUCCESS;
  }

  finishCreation(): void {
    const controlCenterStore = useControlCenterStore();
    if (!this.skipLoaded) controlCenterStore.setLaunchFiles(this.launchFiles);
    else controlCenterStore.addLaunchFiles(this.launchFiles);
    controlCenterStore.saveLaunchFiles(this.skipLoaded);
    super.finishCreation();
    if (this.finalLoad)
      setTimeout(() => {
        this.loadProgressFunc('', 100, true);
      }, 1000);
  }
}
